import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  IconButton,
  Chip,
  Stack,
  Button,
  Tooltip,
  Zoom
} from '@mui/material';
import { ArrowForward, Schedule, WhatsApp, Star, Phone } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ServiceCard = React.memo(({ service, delay = 0 }) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const getServicePath = () => {
    const slug = service.title.toLowerCase().replace(/\s+/g, '-');
    return `/services/${slug}`;
  };

  const handleLearnMore = () => {
    const path = getServicePath();
    navigate(path);
  };

  return (
    <Box
      ref={cardRef}
      sx={{
        transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
        opacity: isVisible ? 1 : 0,
        transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        transitionDelay: `${delay}ms`,
        height: '100%'
      }}
    >
      <Card
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 8px 32px rgba(0,150,136,0.2)'
          }
        }}
      >
        <Box sx={{ position: 'relative', pt: '65%' }}>
          <CardMedia
            component="img"
            image={service.image}
            alt={service.title}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.3s ease',
              transform: isHovered ? 'scale(1.1)' : 'scale(1)'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isHovered 
                ? 'linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0.2))'
                : 'linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))',
              transition: 'all 0.3s ease'
            }}
          />

          {/* Title and Features */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 3,
              transform: isHovered ? 'translateY(100%)' : 'translateY(0)',
              opacity: isHovered ? 0 : 1,
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: 'white',
                fontWeight: 700,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                mb: 1,
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              {service.title}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {service.features?.slice(0, 2).map((feature, index) => (
                <Chip
                  key={index}
                  icon={<Star sx={{ fontSize: 16 }} />}
                  label={feature}
                  size="small"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    backdropFilter: 'blur(4px)',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    height: '24px',
                    '& .MuiChip-icon': {
                      color: '#4CAF50'
                    }
                  }}
                />
              ))}
            </Stack>
          </Box>

          {/* Book Now Button */}
          <Button
            variant="contained"
            onClick={() => navigate('/book-appointment')}
            startIcon={<Schedule />}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: isHovered 
                ? 'translate(-50%, -50%) scale(1)' 
                : 'translate(-50%, -50%) scale(0.9)',
              opacity: isHovered ? 1 : 0,
              background: 'linear-gradient(45deg, #009688, #4CAF50)',
              color: 'white',
              textTransform: 'none',
              borderRadius: '50px',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              zIndex: 2,
              '&:hover': {
                background: 'linear-gradient(45deg, #00897B, #43A047)',
                transform: 'translate(-50%, -50%) scale(1.05)',
                boxShadow: '0 6px 25px rgba(0,0,0,0.4)'
              }
            }}
          >
            Book Now
          </Button>
        </Box>

        <CardContent 
          sx={{ 
            flexGrow: 1,
            p: 3,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 3,
              color: 'text.secondary',
              fontSize: '1rem',
              lineHeight: 1.7
            }}
          >
            {service.description}
          </Typography>

          <Box sx={{ 
            mt: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Button
              onClick={handleLearnMore}
              endIcon={<ArrowForward />}
              sx={{
                color: '#009688',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  background: 'rgba(0, 150, 136, 0.1)',
                  transform: 'translateX(4px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Learn More
            </Button>

            <Tooltip title="Contact via WhatsApp" TransitionComponent={Zoom} arrow>
              <IconButton
                component="a"
                href={`https://wa.me/917619678243?text=Hi, I'm interested in ${service.title} service.`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#25D366',
                  '&:hover': {
                    background: 'rgba(37, 211, 102, 0.1)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <WhatsApp />
              </IconButton>
            </Tooltip>

            <IconButton
              component="a"
              href="tel:+917619678243"
              sx={{
                color: '#000',
                '&:hover': {
                  background: 'rgba(0, 0, 0, 0.1)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <Phone />
            </IconButton>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
});

export default ServiceCard;